import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';

import classNames from 'classnames';

import type { List as ImmutableList } from 'immutable';

import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import { TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

import type { AutosuggestItem } from 'mastodon/components/autosuggest_overlay';
import { AutosuggestOverlay } from 'mastodon/components/autosuggest_overlay';

import { Indent } from '../util/indent_extension';
import type { SuggestionMatch } from '../util/suggestion_trigger';
import { createSuggestionTrigger } from '../util/suggestion_trigger';

import { ComposeToolbar } from './compose_toolbar';

export interface RichTextEditorHandle {
  focus: () => void;
  blur: () => void;
  getHTML: () => string;
  insertText: (text: string) => void;
  setSelectionRange: (start: number, end: number) => void;
  readonly value: string;
}

interface InsertMediaRequest {
  url: string;
  requestId: string;
}

interface RichTextEditorProps {
  value: string;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  lang?: string;
  className?: string;
  suggestions?: ImmutableList<AutosuggestItem>;
  onChange: (html: string) => void;
  onFocus?: (event: FocusEvent) => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  onPaste: (event: ClipboardEvent) => void;
  onDrop?: (event: DragEvent) => void;
  onSuggestionsFetchRequested?: (token: string) => void;
  onSuggestionsClearRequested?: () => void;
  onResolveSuggestion?: (
    token: string,
    suggestion: AutosuggestItem,
  ) => { completion?: string; shouldReplace: boolean };
  insertMediaRequest?: InsertMediaRequest | null;
  onMediaInserted?: () => void;
}

const TRIGGERS: { name: string; char: string }[] = [
  { name: 'mentionSuggestion', char: '@' },
  { name: 'hashtagSuggestion', char: '#' },
  { name: 'emojiSuggestion', char: ':' },
];

export const RichTextEditor = forwardRef<
  RichTextEditorHandle,
  RichTextEditorProps
>(
  (
    {
      value,
      disabled,
      placeholder,
      autoFocus,
      lang,
      className,
      suggestions,
      onChange,
      onFocus,
      onKeyDown,
      onPaste,
      onDrop,
      onSuggestionsFetchRequested,
      onSuggestionsClearRequested,
      onResolveSuggestion,
      insertMediaRequest,
      onMediaInserted,
    },
    ref,
  ) => {
    // Start empty so the first external `value` (a reply prefill mounted before
    // this editor, redraft, edit load, …) always flows through the sync effect
    // below rather than only the `content:` option.
    const lastEmittedHtml = useRef('');

    const [match, setMatch] = useState<SuggestionMatch | null>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const matchRef = useRef<SuggestionMatch | null>(null);
    const selectedIndexRef = useRef(0);
    const suggestionsRef = useRef(suggestions);
    const lastTokenRef = useRef<string | null>(null);
    selectedIndexRef.current = selectedIndex;
    suggestionsRef.current = suggestions;

    const handleSuggestionOpen = useCallback(
      (nextMatch: SuggestionMatch) => {
        matchRef.current = nextMatch;
        setMatch(nextMatch);

        if (nextMatch.token !== lastTokenRef.current) {
          lastTokenRef.current = nextMatch.token;
          setSelectedIndex(0);
          onSuggestionsFetchRequested?.(nextMatch.token);
        }
      },
      [onSuggestionsFetchRequested],
    );

    const handleSuggestionClose = useCallback(() => {
      matchRef.current = null;
      lastTokenRef.current = null;
      setMatch(null);
      onSuggestionsClearRequested?.();
    }, [onSuggestionsClearRequested]);

    const applySuggestion = useCallback(
      (index: number) => {
        const currentMatch = matchRef.current;
        const suggestion = suggestionsRef.current?.get(index);
        if (!currentMatch || !suggestion || !onResolveSuggestion) return;

        const { completion, shouldReplace } = onResolveSuggestion(
          currentMatch.token,
          suggestion,
        );
        currentMatch.apply(completion ?? '', shouldReplace);
      },
      [onResolveSuggestion],
    );

    const handleSuggestionKeyDown = useCallback(
      ({ event }: { event: KeyboardEvent }) => {
        const currentSuggestions = suggestionsRef.current;
        if (!currentSuggestions || currentSuggestions.size === 0) {
          return false;
        }

        switch (event.key) {
          case 'Escape':
            handleSuggestionClose();
            return true;
          case 'ArrowDown':
            setSelectedIndex((i) =>
              Math.min(i + 1, currentSuggestions.size - 1),
            );
            return true;
          case 'ArrowUp':
            setSelectedIndex((i) => Math.max(i - 1, 0));
            return true;
          case 'Enter':
          case 'Tab':
            applySuggestion(selectedIndexRef.current);
            return true;
          default:
            return false;
        }
      },
      [applySuggestion, handleSuggestionClose],
    );

    const editor = useEditor(
      {
        editable: !disabled,
        autofocus: autoFocus ? 'end' : false,
        extensions: [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            link: { openOnClick: false, autolink: true, linkOnPaste: true },
          }),
          Image.configure({ inline: false }),
          Placeholder.configure({ placeholder }),
          Indent,
          ...TRIGGERS.map(({ name, char }) =>
            createSuggestionTrigger(name, char).configure({
              onOpen: handleSuggestionOpen,
              onClose: handleSuggestionClose,
              onKeyDown: handleSuggestionKeyDown,
            }),
          ),
        ],
        content: value,
        editorProps: {
          attributes: {
            class: 'autosuggest-textarea__textarea',
            ...(lang ? { lang } : {}),
            'aria-label': placeholder ?? '',
            dir: 'auto',
          },
          handlePaste: (_view, event) => {
            onPaste(event);
            return event.defaultPrevented;
          },
          handleDrop: (_view, event) => {
            if (onDrop) {
              onDrop(event);
              return event.defaultPrevented;
            }
            return false;
          },
          handleDOMEvents: {
            focus: (_view, event) => {
              onFocus?.(event);
              return false;
            },
            keydown: (_view, event) => {
              if (!event.defaultPrevented) {
                onKeyDown?.(event);
              }
              return false;
            },
          },
        },
        onUpdate: ({ editor: editorInstance }) => {
          const html = editorInstance.getHTML();
          lastEmittedHtml.current = html;
          onChange(html);
        },
      },
      [],
    );

    // Sync content that changed from outside (reply prefill, redraft, edit
    // load, mention insert) without clobbering the editor while the user is
    // actively typing.
    useEffect(() => {
      if (editor.isDestroyed) return;
      if (value === lastEmittedHtml.current) return;
      lastEmittedHtml.current = value;

      // A reply prefill is plain text ending in one or more "@handle" mentions.
      // ProseMirror strips trailing whitespace on parse, and with nothing after
      // the last mention the "@" autocomplete stays open on it, so typed text
      // is absorbed into the handle ("@userTest"). Parse the mentions with no
      // trailing whitespace, then add a real space (a live edit, never
      // collapsed) and drop the caret after it.
      const isPlainText = !/<[a-z][\s\S]*>/i.test(value);
      const endsWithMention = /@[^\s@]+\s*$/.test(value);
      const needsTrailingSpace = isPlainText && endsWithMention;

      editor.commands.setContent(
        needsTrailingSpace ? value.replace(/\s+$/, '') : value,
        { emitUpdate: false },
      );

      if (!needsTrailingSpace) return;

      const settleCaretAfterSpace = () => {
        if (editor.isDestroyed) return;

        editor.commands.command(({ tr, dispatch }) => {
          if (dispatch) {
            // End of the last text block's content (not the document end,
            // which sits outside any paragraph).
            const end = TextSelection.atEnd(tr.doc).to;
            const lastChar = tr.doc.textBetween(Math.max(0, end - 1), end);

            if (lastChar !== ' ') {
              tr.insertText(' ', end);
            }

            tr.setSelection(TextSelection.atEnd(tr.doc));
            // Don't emit onChange for this — it would re-run this effect and
            // cancel the follow-up frame below.
            tr.setMeta('preventUpdate', true);
          }
          return true;
        });
        editor.commands.focus('end');
      };

      // Run it now and again on the next frame: the reply also triggers
      // ComposeForm's own focus() microtask, and whichever runs last wins.
      settleCaretAfterSpace();
      const raf = requestAnimationFrame(settleCaretAfterSpace);
      return () => {
        cancelAnimationFrame(raf);
      };
    }, [editor, value]);

    useEffect(() => {
      if (editor.isDestroyed) return;
      editor.setEditable(!disabled);
    }, [editor, disabled]);

    // An already-uploaded media attachment was requested to also be embedded
    // inline (see the "insert into post" button on a tray thumbnail).
    useEffect(() => {
      if (!insertMediaRequest || editor.isDestroyed) return;
      editor.chain().focus().setImage({ src: insertMediaRequest.url }).run();
      onMediaInserted?.();
    }, [editor, insertMediaRequest, onMediaInserted]);

    useImperativeHandle(
      ref,
      () => ({
        focus: () => {
          editor.commands.focus('end');
        },
        blur: () => {
          editor.commands.blur();
        },
        getHTML: () => editor.getHTML(),
        insertText: (text: string) => {
          editor.chain().focus().insertContent(text).run();
        },
        setSelectionRange: () => {
          // A plain-text caret offset (e.g. "select every mention past the
          // first" on multi-user replies) has no reliable equivalent against
          // an HTML document model; callers fall back to a plain focus.
        },
        get value() {
          return editor.getHTML();
        },
      }),
      [editor],
    );

    const handleSuggestionSelect = useCallback(
      (index: number) => {
        applySuggestion(index);
      },
      [applySuggestion],
    );

    const reference = match
      ? { getBoundingClientRect: () => match.clientRect?.() ?? new DOMRect() }
      : null;

    return (
      <div className={classNames('autosuggest-textarea', className)}>
        <ComposeToolbar editor={editor} />
        <EditorContent editor={editor} />

        {suggestions && (
          <AutosuggestOverlay
            suggestions={suggestions}
            selectedIndex={selectedIndex}
            isOpen={!!match && suggestions.size > 0}
            reference={reference}
            onSelect={handleSuggestionSelect}
            onClose={handleSuggestionClose}
          />
        )}
      </div>
    );
  },
);

RichTextEditor.displayName = 'RichTextEditor';
